'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useState, useTransition } from 'react'
import MatchRow from '@/components/sport/MatchRow'
import type { MatchCenterPayload, SportMatch } from '@/lib/matches-data'
import { getApiBaseUrl } from '@/lib/config'
import { fixturesPath } from '@/lib/standings-logos'
import { formatSiteDate } from '@/lib/site-datetime'

async function loadFixtures(
  lig: string,
  hafta?: number | null,
): Promise<MatchCenterPayload | null> {
  try {
    const q = new URLSearchParams({ lig, mode: 'fixtures' })
    if (hafta != null) q.set('hafta', String(hafta))
    const res = await fetch(`${getApiBaseUrl()}/api/matches?${q}`, {
      cache: 'no-store',
    })
    if (!res.ok) return null
    const json = await res.json()
    return json?.data ?? null
  } catch {
    return null
  }
}

function groupByDay(matches: SportMatch[]) {
  const map = new Map<string, SportMatch[]>()
  for (const m of matches) {
    const key = formatSiteDate(m.utcDate, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
    const list = map.get(key) || []
    list.push(m)
    map.set(key, list)
  }
  return [...map.entries()]
}

export default function FiksturBoard({
  initial,
  lig,
  hafta,
}: {
  initial: MatchCenterPayload
  lig: string
  hafta?: number | null
}) {
  const router = useRouter()
  const [data, setData] = useState(initial)
  /** null = Tümü */
  const [activeWeek, setActiveWeek] = useState<number | null>(
    hafta ?? initial.activeMatchday ?? null,
  )
  const [, startTransition] = useTransition()

  useEffect(() => {
    const tick = () => {
      startTransition(() => {
        void loadFixtures(lig, activeWeek).then((next) => {
          if (next) setData(next)
        })
      })
    }
    const id = window.setInterval(tick, 120_000)
    return () => window.clearInterval(id)
  }, [lig, activeWeek])

  const matchdays = data.matchdays || []

  const weekMatches = useMemo(() => {
    const all = data.allMatches?.length ? data.allMatches : data.matches || []
    if (activeWeek == null) return all
    return all.filter((m) => Number(m.matchday) === activeWeek)
  }, [data.allMatches, data.matches, activeWeek])

  const dayGroups = groupByDay(weekMatches)

  const activeIdx = activeWeek != null ? matchdays.indexOf(activeWeek) : -1
  const prevWeek = activeIdx > 0 ? matchdays[activeIdx - 1] : null
  const nextWeek =
    activeIdx >= 0 && activeIdx < matchdays.length - 1
      ? matchdays[activeIdx + 1]
      : activeWeek == null && matchdays.length
        ? matchdays[0]
        : null

  const selectWeek = (week: number | null) => {
    setActiveWeek(week)
    router.replace(fixturesPath(lig, week == null ? 'all' : week), {
      scroll: false,
    })
  }

  const onSelectChange = (value: string) => {
    if (value === 'all') {
      selectWeek(null)
      return
    }
    const n = parseInt(value, 10)
    if (Number.isFinite(n)) selectWeek(n)
  }

  return (
    <div className="space-y-4">
      {matchdays.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center justify-center gap-2 rounded-2xl border border-gray-100 bg-white px-3 py-3 dark:border-slate-800 dark:bg-slate-900 sm:px-4">
            <button
              type="button"
              disabled={prevWeek == null && activeWeek != null}
              onClick={() => {
                if (activeWeek == null && matchdays.length) {
                  selectWeek(matchdays[matchdays.length - 1])
                  return
                }
                if (prevWeek != null) selectWeek(prevWeek)
              }}
              className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-lg font-bold text-gray-700 enabled:hover:border-red-200 enabled:hover:text-[#D92E2E] disabled:border-transparent disabled:text-gray-300 dark:border-slate-700 dark:text-slate-200 dark:disabled:text-slate-600"
              aria-label="Önceki hafta"
            >
              ‹
            </button>

            <label className="sr-only" htmlFor="fikstur-hafta">
              Hafta seç
            </label>
            <select
              id="fikstur-hafta"
              value={activeWeek == null ? 'all' : String(activeWeek)}
              onChange={(e) => onSelectChange(e.target.value)}
              className="min-w-[9rem] flex-1 appearance-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-center text-sm font-bold text-gray-900 outline-none focus:border-[#D92E2E] dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 sm:flex-none sm:min-w-[12rem]"
            >
              <option value="all">Tümü</option>
              {matchdays.map((week) => (
                <option key={week} value={String(week)}>
                  Hafta {week}
                </option>
              ))}
            </select>

            <button
              type="button"
              disabled={nextWeek == null && activeWeek != null}
              onClick={() => {
                if (activeWeek == null && matchdays.length) {
                  selectWeek(matchdays[0])
                  return
                }
                if (nextWeek != null) selectWeek(nextWeek)
              }}
              className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 text-lg font-bold text-gray-700 enabled:hover:border-red-200 enabled:hover:text-[#D92E2E] disabled:border-transparent disabled:text-gray-300 dark:border-slate-700 dark:text-slate-200 dark:disabled:text-slate-600"
              aria-label="Sonraki hafta"
            >
              ›
            </button>
          </div>

          <nav
            aria-label="Haftalar"
            className="hidden gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:flex"
          >
            <button
              type="button"
              onClick={() => selectWeek(null)}
              className={
                activeWeek == null
                  ? 'shrink-0 rounded-lg bg-[#D92E2E] px-3 py-1.5 text-xs font-bold text-white'
                  : 'shrink-0 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
              }
            >
              Tümü
            </button>
            {matchdays.map((week) => (
              <button
                key={week}
                type="button"
                onClick={() => selectWeek(week)}
                className={
                  week === activeWeek
                    ? 'flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#D92E2E] text-xs font-black text-white'
                    : 'flex size-8 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-xs font-bold text-gray-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200'
                }
              >
                {week}
              </button>
            ))}
          </nav>
        </div>
      ) : null}

      {!weekMatches.length ? (
        <div className="rounded-2xl border border-dashed border-gray-200 px-4 py-10 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
          {activeWeek != null
            ? `Hafta ${activeWeek} için maç yok.`
            : 'Bu lig için fikstür henüz yok.'}
        </div>
      ) : (
        <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white dark:border-slate-800 dark:bg-slate-900">
          {dayGroups.map(([dayLabel, list]) => (
            <div key={dayLabel}>
              <div className="border-b border-gray-100 bg-gray-50/80 px-4 py-2 text-xs font-bold uppercase tracking-wide text-gray-500 dark:border-slate-800 dark:bg-slate-950/50 dark:text-slate-400">
                {dayLabel}
              </div>
              {list.map((m) => (
                <MatchRow key={m.id} match={m} showLeague={false} />
              ))}
            </div>
          ))}
        </section>
      )}
    </div>
  )
}
